import { useState } from 'react';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import { Plus, X } from 'lucide-react';
import { useTranslation } from '@/lib/i18n';
import {
  Button,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
  RichTextEditor,
  Select,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  toast,
} from '@/components/ui';
import { PRODUCT_LOCALES, type ProductFormValues, type ProductLocale } from '../schemas/productSchema';
import { useProductDetailImageUpload } from '../hooks/useProductDetailImageUpload';
import {
  useNoticeSpecTemplatesQuery,
  type NoticeSpecTemplate,
} from '../noticeSpecTemplates/hooks/useNoticeSpecTemplates';
import { editorImageGuideFor } from '@/lib/upload/uploadGuide';

/**
 * Task#6 — 템플릿의 locale 항목 해석: 해당 locale 번역이 있으면 그것, 없으면 ko 정본(items) 폴백.
 * (NoticeSpecTemplateView.translations 계약 — 저장된 비-ko locale 만 포함.)
 */
export const resolveTemplateItemsForLocale = (
  tpl: Pick<NoticeSpecTemplate, 'items' | 'translations'>,
  loc: ProductLocale,
): Record<string, string> => {
  const localized = tpl.translations?.[loc];
  if (localized && Object.keys(localized).length > 0) return localized;
  return tpl.items ?? {};
};

/**
 * ID42 per-locale 상품 정보 고시 항목(noticeSpec Record<string,string>) key-value 에디터.
 * translations.{locale}.noticeSpec 에 바인딩. NoticeSpecEditor(ko 정본 섹션)와 동일 행 편집 패턴.
 *
 * Task#6 — locale-aware 템플릿 적용: 저장된 고시 템플릿을 이 locale 항목(없으면 ko 폴백)으로
 * 시드한다. 기존 입력 키는 보존(추가만) — §11 ko 편집기의 관리 템플릿 적용과 동일 시맨틱.
 * 정적 법정 템플릿(한국법 항목 키)은 ko 전용이라 비-ko picker 에는 관리 템플릿만 노출한다.
 */
const LocaleNoticeSpecEditor = ({ loc }: { loc: ProductLocale }) => {
  const { control } = useFormContext<ProductFormValues>();
  const { t } = useTranslation('products');
  const [selectedManagedId, setSelectedManagedId] = useState('');
  const managedTemplates = useNoticeSpecTemplatesQuery();
  return (
    <Controller
      control={control}
      name={`translations.${loc}.noticeSpec` as const}
      render={({ field }) => {
        const value = (field.value ?? {}) as Record<string, string>;
        const entries = Object.entries(value);

        const replaceAt = (idx: number, key: string, val: string) => {
          const next: Record<string, string> = {};
          entries.forEach(([k, v], i) => {
            if (i === idx) {
              if (key.trim()) next[key] = val;
            } else if (k.trim()) {
              next[k] = v;
            }
          });
          field.onChange(next);
        };
        const removeAt = (idx: number) => {
          const next: Record<string, string> = {};
          entries.forEach(([k, v], i) => {
            if (i !== idx && k.trim()) next[k] = v;
          });
          field.onChange(next);
        };
        const add = () => {
          const next: Record<string, string> = { ...value };
          let placeholder = t('products.fields.noticeSpecNewItem', '신규 항목');
          let n = 1;
          while (placeholder in next) {
            n += 1;
            placeholder = `${t('products.fields.noticeSpecNewItem', '신규 항목')} ${n}`;
          }
          next[placeholder] = '';
          field.onChange(next);
        };

        // Task#6 — locale-aware 관리 템플릿 적용: 이 locale 의 템플릿 항목(없으면 ko 폴백)을
        // 병합한다. 기존 키는 보존, 템플릿에만 있는 키를 값까지 추가(§11 applyManagedTemplate 동일).
        const applyManagedTemplate = () => {
          const tpl = (managedTemplates.data ?? []).find((m) => m.templateId === selectedManagedId);
          if (!tpl) return;
          const source = resolveTemplateItemsForLocale(tpl, loc);
          const next: Record<string, string> = { ...value };
          let addedCount = 0;
          for (const [k, v] of Object.entries(source)) {
            if (!Object.prototype.hasOwnProperty.call(next, k)) {
              next[k] = v;
              addedCount += 1;
            }
          }
          field.onChange(next);
          toast.success(
            addedCount > 0
              ? t('products.fields.noticeSpecTemplateApplied', '{{label}} 템플릿 {{count}}개 항목을 추가했습니다.')
                  .replace('{{label}}', tpl.name)
                  .replace('{{count}}', String(addedCount))
              : t('products.fields.noticeSpecTemplateNoChange', '{{label}} 템플릿 항목이 이미 모두 추가되어 있습니다.')
                  .replace('{{label}}', tpl.name),
          );
        };

        return (
          <div className="flex flex-col gap-2">
            <FormLabel>{t('products.fields.translationNoticeSpec', '상품 정보 고시 (항목별)')}</FormLabel>

            {/* Task#6 — locale-aware 저장된 템플릿 적용(이 언어 항목 없으면 한국어 항목 폴백). */}
            <div
              className="flex flex-wrap items-center gap-2 rounded-md p-2.5"
              style={{ background: 'var(--surface-0)', border: '1px solid var(--divider-strong)' }}
              data-testid={`locale-notice-template-picker-${loc}`}
            >
              <span className="text-[12px]" style={{ color: 'var(--content-muted)' }}>
                {t('products.fields.noticeSpecManagedLabel', '저장된 템플릿')}
              </span>
              <Select
                aria-label={t('products.fields.noticeSpecManagedSelect', '저장된 고시 템플릿 선택')}
                value={selectedManagedId}
                onChange={(e) => setSelectedManagedId(e.target.value)}
                disabled={managedTemplates.isLoading}
                style={{ width: 'auto', minWidth: '200px', flex: '0 1 auto' }}
              >
                <option value="">
                  {managedTemplates.isLoading
                    ? t('products.fields.noticeSpecManagedLoading', '불러오는 중…')
                    : t('products.fields.noticeSpecManagedPlaceholder', '템플릿 선택')}
                </option>
                {(managedTemplates.data ?? []).map((tpl) => (
                  <option key={tpl.templateId} value={tpl.templateId}>
                    {tpl.name}
                  </option>
                ))}
              </Select>
              <Button
                type="button"
                variant="secondary"
                size="sm"
                disabled={!selectedManagedId}
                onClick={applyManagedTemplate}
              >
                {t('products.fields.noticeSpecTemplateApply', '템플릿 적용')}
              </Button>
              <span className="text-[12px]" style={{ color: 'var(--content-muted)' }}>
                {t(
                  'products.fields.noticeSpecManagedLocaleHint',
                  '이 언어의 템플릿 항목을 적용합니다. 이 언어 항목이 없는 템플릿은 한국어 항목을 적용합니다.',
                )}
              </span>
            </div>
            {entries.length === 0 ? (
              <div
                className="flex items-center justify-center rounded-md py-4 text-[12px]"
                style={{ background: 'var(--surface-0)', border: '1px dashed var(--divider-strong)', color: 'var(--content-muted)' }}
              >
                {t('products.fields.translationNoticeSpecEmpty', '등록된 고시 항목이 없습니다 (선택).')}
              </div>
            ) : (
              <ul className="flex flex-col gap-2">
                {entries.map(([key, val], idx) => (
                  <li key={`${idx}-${key}`} className="grid items-center gap-2" style={{ gridTemplateColumns: '180px 1fr auto' }}>
                    <Input
                      type="text"
                      defaultValue={key}
                      placeholder={t('products.fields.noticeSpecKey', '항목명 (예: 원산지)')}
                      onBlur={(e) => replaceAt(idx, e.target.value, val)}
                    />
                    <Input
                      type="text"
                      defaultValue={val}
                      placeholder={t('products.fields.noticeSpecValue', '값')}
                      onChange={(e) => replaceAt(idx, key, e.target.value)}
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => removeAt(idx)}
                      aria-label={t('products.fields.noticeSpecRemove', '삭제')}
                    >
                      <X size={14} style={{ color: 'var(--signal-danger)' }} />
                    </Button>
                  </li>
                ))}
              </ul>
            )}
            <div>
              <Button type="button" variant="secondary" size="sm" onClick={add}>
                <Plus size={14} />
                {t('products.fields.noticeSpecAdd', '항목 추가')}
              </Button>
            </div>
          </div>
        );
      }}
    />
  );
};

const LOCALE_LABELS: Record<ProductLocale, string> = {
  'ko-KR': '한국어',
  'en-US': 'English',
  'ja-JP': '日本語',
  'zh-CN': '中文',
};

export interface TranslationsTabsProps {
  /** EVENT 타입 상품일 때만 eventPromoSubtitle 입력 노출 */
  showEventSubtitle?: boolean;
}

export const TranslationsTabs = ({ showEventSubtitle = false }: TranslationsTabsProps) => {
  const { control, getValues, setValue } = useFormContext<ProductFormValues>();
  const { t } = useTranslation('products');
  // ID42 상세 본문 다국어 — 모든 로케일 에디터가 공유하는 이미지 업로드 핸들러(PRODUCT_DETAIL).
  const uploadDetailImage = useProductDetailImageUpload();
  // ID42 #14: variant(옵션/재고 SKU) 라벨 다국어 — 상관키=variantCode. PdpOptionSheet 가 렌더하는
  // 사용자 노출 옵션 라벨을 ko/en/ja/zh 로 입력. translations.{locale}.variants[]{variantCode,label}.
  // (product_options(optionId) 옵션명은 BO 옵션관리 UI 부재로 별도 보류 — options[] omit.)
  const variants = useWatch({ control, name: 'variants' });
  const translations = useWatch({ control, name: 'translations' });

  /** 특정 locale/variantCode 의 현재 번역 라벨. */
  const variantLabelFor = (loc: ProductLocale, variantCode: string): string =>
    translations?.[loc]?.variants?.find((v) => v.variantCode === variantCode)?.label ?? '';

  /** translations.{loc}.variants[] 를 variantCode 기준 find-or-append upsert (immutable). */
  const setVariantLabel = (loc: ProductLocale, variantCode: string, label: string) => {
    const current = getValues(`translations.${loc}.variants`) ?? [];
    const idx = current.findIndex((v) => v.variantCode === variantCode);
    const next =
      idx >= 0
        ? current.map((v, i) => (i === idx ? { ...v, label } : v))
        : [...current, { variantCode, label }];
    setValue(`translations.${loc}.variants`, next, { shouldDirty: true });
  };

  return (
    <Tabs defaultValue="ko-KR" className="w-full">
      <TabsList className="mb-4">
        {PRODUCT_LOCALES.map((loc) => (
          <TabsTrigger key={loc} value={loc}>
            {LOCALE_LABELS[loc]}
            {loc === 'ko-KR' ? (
              <span aria-hidden="true" className="ml-1" style={{ color: 'var(--signal-danger)' }}>
                *
              </span>
            ) : null}
          </TabsTrigger>
        ))}
      </TabsList>

      {PRODUCT_LOCALES.map((loc) => (
        <TabsContent key={loc} value={loc} className="flex flex-col gap-4">
          {/* D3 정본 일원화 — 한국어 상품명은 §1 기본정보(nameKo)가 유일한 입력 소스.
              ko 탭의 중복 입력란을 제거하고 안내만 남긴다(저장 시 mapper 가 nameKo 를 ko 번역에 미러링). */}
          {loc === 'ko-KR' ? (
            <p className="text-[12px]" style={{ color: 'var(--content-muted)' }}>
              {t(
                'products.fields.translationNameKoUnified',
                '한국어 상품명은 ①상품 기본정보의 “상품명 (한국어)”에서 입력합니다. 저장 시 자동 동기화됩니다.',
              )}
            </p>
          ) : (
            <FormField
              control={control}
              name={`translations.${loc}.name` as const}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('products.fields.translationName', '상품명')}</FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      value={field.value ?? ''}
                      placeholder={t('products.fields.translationNamePlaceholder', '해당 언어의 상품명을 입력하세요')}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          )}

          <FormField
            control={control}
            name={`translations.${loc}.brandName` as const}
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t('products.fields.translationBrandName', '브랜드명')}</FormLabel>
                <FormControl>
                  <Input
                    {...field}
                    value={field.value ?? ''}
                    placeholder={t('products.fields.translationBrandNamePlaceholder', '해당 언어 브랜드 표기 (선택)')}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={control}
            name={`translations.${loc}.description` as const}
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t('products.fields.translationDescription', '간단 설명')}</FormLabel>
                <FormControl>
                  <textarea
                    {...field}
                    value={field.value ?? ''}
                    rows={4}
                    placeholder={t('products.fields.translationDescriptionPlaceholder', '상품 상세에서 상품명 아래에 보입니다. 발매일을 문구로 쓸 때는 여기에 적고 기본 정보의 발매일(날짜)은 비워 주세요. 예: 발매일 : 2026년 8월 18일부터 순차 출고 예정')}
                    className="block w-full resize-y rounded-md px-3.5 py-2 text-[14px] outline-none transition-colors focus-visible:outline-none focus-visible:[box-shadow:var(--focus-ring-brand)]"
                    style={{
                      background: 'var(--surface-0)',
                      border: '1px solid var(--divider-strong)',
                      color: 'var(--content-strong)',
                      minHeight: 96,
                    }}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          {showEventSubtitle ? (
            <FormField
              control={control}
              name={`translations.${loc}.eventPromoSubtitle` as const}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('products.fields.translationEventSubtitle', '이벤트 카드 부제')}</FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      value={field.value ?? ''}
                      maxLength={120}
                      placeholder={t('products.fields.translationEventSubtitlePlaceholder', 'EVENT 카드 빨강 리본에 표시 (선택)')}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          ) : null}

          {/* ID42 상세 설명(detailHtml) 다국어 — ko-KR 정본(상품 상세 페이지 본문),
              en/ja/zh 는 비우면 한국어 상세로 폴백. LocaleHtmlTabs(CMS) 와 동일한 공용
              RichTextEditor(HTML 소스 직접 편집 + 이미지 업로드) 를 로케일 탭마다 렌더한다.
              빈 본문(`<p></p>`)은 '' 로 정규화해 BE 가 ko 폴백을 적용하도록 한다. */}
          <FormField
            control={control}
            name={`translations.${loc}.detailHtml` as const}
            render={({ field }) => (
              <FormItem>
                <FormLabel>
                  {loc === 'ko-KR'
                    ? t('products.fields.translationDetailHtmlBase', '상세 본문')
                    : t('products.fields.translationDetailHtml', '상세 본문')}
                  {loc === 'ko-KR' ? (
                    <span aria-hidden="true" className="ml-1" style={{ color: 'var(--signal-danger)' }}>
                      *
                    </span>
                  ) : null}
                </FormLabel>
                {loc !== 'ko-KR' ? (
                  <p className="text-[12px]" style={{ color: 'var(--content-muted)' }}>
                    {t('products.fields.translationDetailHtmlFallbackHint', '비우면 한국어(정본) 상세 본문이 노출됩니다.')}
                  </p>
                ) : null}
                <FormControl>
                  <RichTextEditor
                    value={field.value ?? ''}
                    onChange={(html) => field.onChange(html === '<p></p>' ? '' : html)}
                    minHeight={220}
                    aria-label={`${t('products.fields.translationDetailHtml', '상세 본문')} (${LOCALE_LABELS[loc]})`}
                    onUploadImage={uploadDetailImage}
                    imageGuide={editorImageGuideFor('PRODUCT_DETAIL_BODY')}
                    onUploadError={(e) =>
                      toast.error(e instanceof Error ? e.message : '이미지 업로드에 실패했습니다.')
                    }
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          {/* D2 — noticeText 는 SF PDP "교환 및 반품안내" 본문(PdpPanels NoticePanel, 비우면 기본
              안내문 폴백). 종전 라벨 "상품 정보 고시"가 §11 고시 섹션의 중복 입력처럼 보이게 한
              오표기 — 실제 노출 위치 기준으로 정명(wire 필드/데이터 무변경). */}
          <FormField
            control={control}
            name={`translations.${loc}.noticeText` as const}
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t('products.fields.translationNoticeText', '교환/반품 안내')}</FormLabel>
                <FormControl>
                  <textarea
                    {...field}
                    value={field.value ?? ''}
                    rows={4}
                    placeholder={t('products.fields.translationNoticeTextPlaceholder', '해당 언어 교환/반품 안내 (비우면 기본 안내문 노출)')}
                    className="block w-full resize-y rounded-md px-3.5 py-2 text-[14px] outline-none transition-colors focus-visible:outline-none focus-visible:[box-shadow:var(--focus-ring-brand)]"
                    style={{
                      background: 'var(--surface-0)',
                      border: '1px solid var(--divider-strong)',
                      color: 'var(--content-strong)',
                      minHeight: 96,
                    }}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          {/* ID42 상품 정보 고시 항목(noticeSpec Record) 로케일별 — noticeText(자유텍스트)와 별개 키.
              D1 정본 일원화 — ko 고시 항목은 §11 "상품 정보 고시" 템플릿 섹션이 유일한 입력 소스라
              ko 탭에서는 중복 편집기를 제거하고 안내만 남긴다(저장 시 mapper 가 ko 번역에 미러링). */}
          {loc === 'ko-KR' ? (
            <p className="text-[12px]" style={{ color: 'var(--content-muted)' }}>
              {t(
                'products.fields.translationNoticeSpecKoUnified',
                '한국어 고시 항목은 “상품 정보 고시” 섹션(템플릿)에서 입력합니다. 저장 시 자동 동기화됩니다.',
              )}
            </p>
          ) : (
            <LocaleNoticeSpecEditor loc={loc} />
          )}

          {/* D9 — policyText 는 SF PDP "고객센터 CS안내" 본문(비우면 기본 CS 안내 폴백),
              shippingPolicyText 는 "배송안내" 수동 오버라이드(비우면 §7 배송비 표기 기반 자동 생성).
              실제 노출 위치 기준으로 정명(wire 필드/데이터 무변경). */}
          <FormField
            control={control}
            name={`translations.${loc}.policyText` as const}
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t('products.fields.translationPolicyText', '고객센터(CS) 안내')}</FormLabel>
                <FormControl>
                  <textarea
                    {...field}
                    value={field.value ?? ''}
                    rows={3}
                    placeholder={t('products.fields.translationPolicyTextPlaceholder', '해당 언어 고객센터/문의 안내 (비우면 기본 CS 안내 노출)')}
                    className="block w-full resize-y rounded-md px-3.5 py-2 text-[14px] outline-none transition-colors focus-visible:outline-none focus-visible:[box-shadow:var(--focus-ring-brand)]"
                    style={{ background: 'var(--surface-0)', border: '1px solid var(--divider-strong)', color: 'var(--content-strong)', minHeight: 72 }}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={control}
            name={`translations.${loc}.shippingPolicyText` as const}
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t('products.fields.translationShippingPolicyText', '배송 안내 (수동 입력)')}</FormLabel>
                <FormControl>
                  <textarea
                    {...field}
                    value={field.value ?? ''}
                    rows={3}
                    placeholder={t('products.fields.translationShippingPolicyTextPlaceholder', '비우면 §7 배송비 표기 기반 자동 안내가 노출됩니다 (입력 시 우선)')}
                    className="block w-full resize-y rounded-md px-3.5 py-2 text-[14px] outline-none transition-colors focus-visible:outline-none focus-visible:[box-shadow:var(--focus-ring-brand)]"
                    style={{ background: 'var(--surface-0)', border: '1px solid var(--divider-strong)', color: 'var(--content-strong)', minHeight: 72 }}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          {/* ID42 #14 옵션(variant) 라벨 다국어 — translations.{locale}.variants[]{variantCode,label}.
              ko-KR 은 옵션/재고 섹션 라벨이 정본이라 제외. variantCode 없는 신규 옵션은 저장 후 번역. */}
          {loc !== 'ko-KR' && Array.isArray(variants) && variants.length > 0 ? (
            <div className="flex flex-col gap-2 border-t pt-4" style={{ borderColor: 'var(--divider-soft)' }}>
              <span className="text-[13px] font-medium" style={{ color: 'var(--content-strong)' }}>
                {t('products.fields.translationVariantNames', '옵션(변이) 라벨 번역')}
              </span>
              <ul className="flex flex-col gap-2">
                {variants.map((variant, idx) =>
                  variant?.variantCode ? (
                    <li key={variant.variantCode} className="grid items-center gap-2" style={{ gridTemplateColumns: '160px 1fr' }}>
                      <span className="truncate text-[13px]" style={{ color: 'var(--content-muted)' }} title={variant.label ?? ''}>
                        {variant.label || t('products.fields.translationOptionUnnamed', '(옵션명 없음)')}
                      </span>
                      <Input
                        value={variantLabelFor(loc, variant.variantCode)}
                        maxLength={100}
                        placeholder={variant.label || t('products.fields.translationVariantPlaceholder', '해당 언어 옵션 라벨 (선택)')}
                        aria-label={t('products.fields.translationVariantAria', '옵션 라벨 번역')}
                        onChange={(e) => setVariantLabel(loc, variant.variantCode as string, e.target.value)}
                      />
                    </li>
                  ) : (
                    <li key={idx} className="grid items-center gap-2 opacity-60" style={{ gridTemplateColumns: '160px 1fr' }}>
                      <span className="truncate text-[13px]" style={{ color: 'var(--content-muted)' }} title={variant?.label ?? ''}>
                        {variant?.label || t('products.fields.translationOptionUnnamed', '(옵션명 없음)')}
                      </span>
                      <span className="text-[12px]" style={{ color: 'var(--content-muted)' }}>
                        {t('products.fields.translationVariantNewHint', '저장 후 옵션 라벨을 번역할 수 있습니다.')}
                      </span>
                    </li>
                  ),
                )}
              </ul>
            </div>
          ) : null}
        </TabsContent>
      ))}
    </Tabs>
  );
};
